import {
  labelPlaceholderColor,
  labelTextDecoration,
  useCanvasLabelStyle,
} from "../hooks/useCanvasLabelStyle";
import type { TextPlacementGhost } from "../hooks/useTextLabelPlacement";
import {
  TEXT_LABEL_PLACEHOLDER,
  TEXT_LABEL_TEXT_PADDING,
} from "../types/canvasNodeData";

/**
 * The label about to be placed: same box, inset, font and prompt as the node
 * that replaces it, at the position it will be dropped on, wearing every
 * setting the Text tray can make — face, size, weight, italic, underline,
 * strikethrough, alignment and colour (ROUND2 §4). A leaf so the
 * sticky-style subscription re-renders nothing else.
 */
export function TextLabelPlacementGhost({
  ghost,
}: {
  ghost: TextPlacementGhost;
}) {
  const labelStyle = useCanvasLabelStyle();
  const prompt = {
    fontFamily: labelStyle.font_family,
    fontSize: labelStyle.font_size,
    fontWeight: labelStyle.is_bold ? "bold" : 400,
    fontStyle: labelStyle.is_italic ? "italic" : "normal",
    textDecoration: labelTextDecoration(labelStyle),
    textAlign: labelStyle.align,
    color: labelPlaceholderColor(labelStyle.text_color),
    padding: TEXT_LABEL_TEXT_PADDING,
    whiteSpace: "pre" as const,
  };

  if (ghost.dragWidth) {
    // Drag-to-width: the box the label will fill, the prompt aligned inside
    // it the way the text will be.
    return (
      <div
        className="canvas-label-ghost-preview absolute pointer-events-none select-none"
        data-ghost="drag"
        style={{
          left: ghost.position.x,
          top: ghost.position.y,
          width: ghost.dragWidth,
          minHeight: 32,
          border: "2px dashed hsl(var(--primary) / 0.6)",
          borderRadius: "4px",
          backgroundColor: "hsl(var(--primary) / 0.05)",
        }}
      >
        <div style={prompt}>{TEXT_LABEL_PLACEHOLDER}</div>
      </div>
    );
  }

  return (
    <div
      className="canvas-label-ghost-preview absolute pointer-events-none select-none"
      style={{ left: ghost.position.x, top: ghost.position.y }}
    >
      <div className="absolute -inset-px rounded-[3px] border-2 border-canvas-selection/60" />
      <div style={prompt}>{TEXT_LABEL_PLACEHOLDER}</div>
    </div>
  );
}
